/**
 * Stable, opaque identity for unauthenticated browser sessions.
 *
 * This is an abuse-limiting/storage key, not authentication: callers can replace
 * it. The backend hashes the value before using it as a principal.
 */

// Reuse the existing browser-user key so old and new frontend state do not
// maintain two competing anonymous identifiers.
export const GUEST_ID_STORAGE_KEY = 'trinity_user_id';
export const GUEST_ID_HEADER = 'X-Trinity-Guest-ID';

const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

let cachedGuestId: string | null = null;

export function isValidGuestId(value: string | null | undefined): value is string {
  return typeof value === 'string' && UUID_V4_PATTERN.test(value);
}

function generateGuestId(): string {
  const cryptoApi = globalThis.crypto;
  if (!cryptoApi?.getRandomValues) {
    throw new Error('Secure browser randomness is unavailable');
  }

  const bytes = new Uint8Array(16);
  cryptoApi.getRandomValues(bytes);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-');
}

function getLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function getOrCreateGuestId(): string {
  if (isValidGuestId(cachedGuestId)) return cachedGuestId;

  const storage = getLocalStorage();
  try {
    const storedGuestId = storage?.getItem(GUEST_ID_STORAGE_KEY);
    if (isValidGuestId(storedGuestId)) {
      cachedGuestId = storedGuestId.toLowerCase();
      return cachedGuestId;
    }
  } catch {
    // Continue with an in-memory identity when storage access is unavailable.
  }

  cachedGuestId = generateGuestId();
  try {
    storage?.setItem(GUEST_ID_STORAGE_KEY, cachedGuestId);
  } catch {
    // The in-memory cache keeps the identity stable for this page session.
  }
  return cachedGuestId;
}

export function getGuestRequestHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    [GUEST_ID_HEADER]: getOrCreateGuestId(),
  };
}
